import React, { useCallback, useEffect, useState } from 'react';
import { Image as ImageIcon, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  appearanceApi, errorText, partnerApi,
} from '../lib/emsportsPartner';
import { Busy, Empty, Field, JsonEditor, Panel, Problem, Toggle } from './EmSportsBits';
import ConfirmDialog from './ConfirmDialog';

/**
 * EveryMatrix — Görünüm sekmesi (tema, banner, içerik alanları).
 *
 * Üç şey aynı yerde durur çünkü üçü de oyuncunun gördüğü **tek** ekranı
 * boyar ve hepsi sağlayıcının kaydındadır; bizim veritabanımızda
 * karşılıkları yoktur. Bu yüzden sayfa her açıldığında sağlayıcıdan okunur
 * ve yazma da doğrudan oraya gider — yerel bir kopya tutmak, panelde seçili
 * görünüp oyuncuda uygulanmamış bir tema bırakırdı (Digitain'de tam olarak
 * bu ölçülmüştü).
 *
 * Görseller **bizim tarafımızda** durur; sağlayıcıya yalnızca adres gider ve
 * adres **mutlak** olmalıdır: spor kitabı kurulumun kendi alt alan adından
 * sunulduğu için göreli bir yol ana siteye değil spor kitabının köküne
 * çözülür ve görsel oyuncuda hiç çizilmez.
 */

const BANNER_SLOTS = [
  { value: 'center-main-slider', label: 'Ana slider (orta üst)' },
  { value: 'top_middle_1', label: 'Üst orta şerit' },
  { value: 'bottom_right_1', label: 'Kupon altı (küçük)' },
  { value: 'bottom_right_2', label: 'Kupon altı (uzun kolon)' },
];

const EMPTY_BANNER = {
  slot: 'center-main-slider',
  image_url: '',
  image_url_mobile: '',
  link_url: '',
  title: '',
  sort_order: 0,
  is_active: true,
};

const EMPTY_BLOCK = {
  location: '',
  lang: 'tr',
  device: '',
  title: '',
  sort_order: 0,
  is_active: true,
  items: [{ image_url: '', link_url: '', alt: '' }],
};

const rowId = (row) => String(row?.id ?? row?.banner_id ?? row?.block_id ?? '');

const EmSportsAppearance = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');

  const [banners, setBanners] = useState([]);
  const [slots, setSlots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [draftBanner, setDraftBanner] = useState(EMPTY_BANNER);
  const [draftBlock, setDraftBlock] = useState(EMPTY_BLOCK);
  // Silme geri alınamaz: banner ve içerik alanı sağlayıcıda durur,
  // bizde kopyası yoktur.
  const [pending, setPending] = useState(null);

  const [theme, setTheme] = useState(null);
  const [themeDraft, setThemeDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [bannerRes, cmsRes] = await Promise.all([
        appearanceApi.banners().catch((error) => ({ error })),
        appearanceApi.cms().catch((error) => ({ error })),
      ]);
      if (bannerRes.error) throw bannerRes.error;
      setBanners(bannerRes.data?.items || []);
      if (!cmsRes.error) {
        setSlots(cmsRes.data?.slots || []);
        setBlocks(cmsRes.data?.blocks || []);
      }
      // Tema ayrı bir yüzeydedir (partner API) ve kapalı olabilir; kapalıysa
      // banner yönetimi yine çalışmalıdır.
      try {
        const { data } = await partnerApi.theme();
        setTheme(data);
        setThemeDraft(data?.theme && typeof data.theme === 'object'
          ? data.theme : {});
      } catch {
        setTheme(null);
      }
    } catch (error) {
      setProblem(errorText(error, 'Görünüm bilgileri alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, action, successTitle) => {
    setBusy(key);
    try {
      await action();
      if (successTitle) toast({ title: successTitle });
      await load();
    } catch (error) {
      toast({
        title: 'İşlem tamamlanamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Tema ve logo şeridi"
        description={
          'Renk değişkenleri sağlayıcının kaydındadır ve SPA oradan boyanır. '
          + 'Önizleme kaydetmez; yeni tema oyuncu sayfayı yenilediğinde '
          + 'görünür. Tanınmayan bir anahtar 422 ile reddedilir — '
          + 'BetConstruct biçimindeki "scheme" alanı burada geçersizdir.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} aria-hidden="true" /> Yenile
          </button>
        )}
      >
        {theme === null ? (
          <Empty>
            Tema yüzeyi bu merchant için kapalı görünüyor (Partner API).
            Sağlayıcı panelinden açtırabilirsiniz.
          </Empty>
        ) : (
          <div className="space-y-2">
            <JsonEditor
              value={themeDraft}
              onChange={setThemeDraft}
              rows={10}
              hint={
                'Örnek: {"preset": "navy-amber", "vars": '
                + '{"--c-base-01": "#e11d48"}, "fullWidth": true}'
              }
            />
            <button
              type="button"
              disabled={busy === 'theme'}
              className="admin-btn-primary inline-flex items-center gap-2 !py-1 !text-[11px]"
              onClick={() => run(
                'theme',
                () => partnerApi.saveTheme({ theme: themeDraft }),
                'Tema sağlayıcıya yazıldı',
              )}
            >
              {busy === 'theme'
                ? <Loader2 size={12} className="animate-spin" />
                : null}
              Temayı uygula
            </button>
          </div>
        )}
      </Panel>

      <Panel
        title="Banner (slider)"
        description={
          'Görseller sizin CDN’inizde durur; sağlayıcıya yalnızca adres '
          + 'gider ve adres https:// ile başlamalıdır. Mobil için ayrı görsel '
          + 'vermeniz önerilir: geniş masaüstü görseli dar ekranda kırpılır.'
        }
      >
        {banners.length ? (
          <ul className="ems-rows">
            {banners.map((row) => (
              <li
                key={rowId(row) || row.image_url}
                className="ems-row"
              >
                {row.image_url ? (
                  <img
                    src={row.image_url}
                    alt=""
                    width={78}
                    height={42}
                    loading="lazy"
                    className="ems-thumb"
                  />
                ) : (
                  <span className="ems-thumb ems-thumb-empty">
                    <ImageIcon size={14} aria-hidden="true" />
                  </span>
                )}
                <div className="ems-row-main">
                  <div className="ems-row-title">
                    {row.title || row.slot || 'Banner'}
                  </div>
                  <div className="ems-row-sub">
                    {row.slot} · {row.link_url || 'bağlantısız'}
                  </div>
                </div>
                <label className="ems-toggle">
                  <input
                    type="checkbox"
                    checked={row.is_active !== false}
                    onChange={(event) => run(
                      `banner:${rowId(row)}`,
                      () => appearanceApi.saveBanner(rowId(row), {
                        is_active: event.target.checked,
                      }),
                      'Banner güncellendi',
                    )}
                  />
                  Yayında
                </label>
                <button
                  type="button"
                  className="admin-btn-danger ems-row-end"
                  aria-label={`${row.title || row.slot || 'Banner'} — sil`}
                  disabled={busy === `banner:${rowId(row)}`}
                  onClick={() => setPending({ kind: 'banner', row })}
                >
                  <Trash2 size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Bu merchant için kayıtlı banner yok.</Empty>
        )}

        <div className="ems-form">
          <Field label="Alan">
            <select
              className="admin-input"
              value={draftBanner.slot}
              onChange={(event) => setDraftBanner(
                (old) => ({ ...old, slot: event.target.value }))}
            >
              {BANNER_SLOTS.map((slot) => (
                <option key={slot.value} value={slot.value}>{slot.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Başlık">
            <input
              className="admin-input"
              value={draftBanner.title}
              onChange={(event) => setDraftBanner(
                (old) => ({ ...old, title: event.target.value }))}
            />
          </Field>
          <Field label="Görsel adresi" hint="https:// ile başlamalıdır">
            <input
              className="admin-input"
              placeholder="https://cdn.siteniz.com/banner.jpg"
              value={draftBanner.image_url}
              onChange={(event) => setDraftBanner(
                (old) => ({ ...old, image_url: event.target.value }))}
            />
          </Field>
          <Field label="Mobil görsel (önerilir)">
            <input
              className="admin-input"
              placeholder="https://cdn.siteniz.com/banner-mobil.jpg"
              value={draftBanner.image_url_mobile}
              onChange={(event) => setDraftBanner(
                (old) => ({ ...old, image_url_mobile: event.target.value }))}
            />
          </Field>
          <Field label="Bağlantı" hint="Site içi yol (/tr/) ya da https:// adres">
            <input
              className="admin-input"
              value={draftBanner.link_url}
              onChange={(event) => setDraftBanner(
                (old) => ({ ...old, link_url: event.target.value }))}
            />
          </Field>
          <Field label="Sıra">
            <input
              type="number"
              min={0}
              className="admin-input"
              value={draftBanner.sort_order}
              onChange={(event) => setDraftBanner((old) => ({
                ...old, sort_order: Number(event.target.value) || 0,
              }))}
            />
          </Field>
          <div className="ems-form-wide">
            <button
              type="button"
              disabled={busy === 'banner:new' || !draftBanner.image_url}
              className="admin-btn-primary inline-flex items-center gap-2 !py-1 !text-[11px]"
              onClick={() => run(
                'banner:new',
                async () => {
                  await appearanceApi.createBanner(draftBanner);
                  setDraftBanner(EMPTY_BANNER);
                },
                'Banner eklendi',
              )}
            >
              <Plus size={12} /> Banner ekle
            </button>
          </div>
        </div>
      </Panel>

      <Panel
        title="İçerik alanları"
        description={
          'Spor kitabının boş alanlarına (sol üst promo kolonu, kupon altı, '
          + 'üst orta şerit) istediğiniz kadar görsel konur; her görselin '
          + 'açtığı bağlantı ayrı verilir. Alanların ölçüleri sağlayıcıdan '
          + 'okunur — ölçüsüne uymayan görsel oyuncuda kırpılır.'
        }
      >
        {slots.length > 0 && (
          <div className="ems-slots">
            {slots.map((slot) => (
              <span
                key={slot.id}
                className="ems-slot"
              >
                <b>{slot.id}</b>
                {slot.label ? ` · ${slot.label}` : ''}
                {slot.width ? ` · ${slot.width}px` : ''}
                {slot.height ? `×${slot.height}` : ''}
              </span>
            ))}
          </div>
        )}

        {blocks.length ? (
          <ul className="ems-rows">
            {blocks.map((row) => (
              <li
                key={rowId(row)}
                className="ems-row"
              >
                <div className="ems-row-main">
                  <div className="ems-row-title">
                    {row.title || row.location}
                  </div>
                  <div className="ems-row-sub">
                    {row.location} · {(row.items || []).length} görsel
                    {row.device ? ` · ${row.device}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-btn-danger ems-row-end"
                  aria-label={`${row.title || row.location} — sil`}
                  disabled={busy === `cms:${rowId(row)}`}
                  onClick={() => setPending({ kind: 'cms', row })}
                >
                  <Trash2 size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Henüz içerik alanı tanımlanmamış.</Empty>
        )}

        <div className="ems-form">
          <Field label="Alan kimliği" hint="Yukarıdaki listeden seçin">
            <input
              className="admin-input"
              placeholder="top_left_1"
              value={draftBlock.location}
              onChange={(event) => setDraftBlock(
                (old) => ({ ...old, location: event.target.value }))}
            />
          </Field>
          <Field label="Başlık">
            <input
              className="admin-input"
              value={draftBlock.title}
              onChange={(event) => setDraftBlock(
                (old) => ({ ...old, title: event.target.value }))}
            />
          </Field>
          <Field label="Görsel adresi">
            <input
              className="admin-input"
              placeholder="https://cdn.siteniz.com/promo.jpg"
              value={draftBlock.items[0].image_url}
              onChange={(event) => setDraftBlock((old) => ({
                ...old,
                items: [{ ...old.items[0], image_url: event.target.value }],
              }))}
            />
          </Field>
          <Field label="Bağlantı">
            <input
              className="admin-input"
              placeholder="/promotions"
              value={draftBlock.items[0].link_url}
              onChange={(event) => setDraftBlock((old) => ({
                ...old,
                items: [{ ...old.items[0], link_url: event.target.value }],
              }))}
            />
          </Field>
          <div className="ems-form-wide">
            <Toggle
              label="Yalnızca mobilde göster"
              hint="Kapalıyken bütün cihazlarda çizilir."
              checked={draftBlock.device === 'small'}
              onChange={(checked) => setDraftBlock((old) => ({
                ...old, device: checked ? 'small' : '',
              }))}
            />
          </div>
          <div className="ems-form-wide">
            <button
              type="button"
              disabled={
                busy === 'cms:new'
                || !draftBlock.location
                || !draftBlock.items[0].image_url
              }
              className="admin-btn-primary inline-flex items-center gap-2 !py-1 !text-[11px]"
              onClick={() => run(
                'cms:new',
                async () => {
                  await appearanceApi.createBlock(draftBlock);
                  setDraftBlock(EMPTY_BLOCK);
                },
                'İçerik alanı kaydedildi',
              )}
            >
              <Plus size={12} /> İçerik alanı ekle
            </button>
          </div>
        </div>
      </Panel>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.kind === 'cms' ? 'İçerik alanını sil' : 'Banner’ı sil'}
        description={pending
          ? `${pending.row.title || pending.row.slot || pending.row.location || 'Kayıt'} sağlayıcıdan kalıcı olarak silinecek; bizde kopyası durmaz.`
          : ''}
        confirmLabel="Sil"
        tone="danger"
        busy={Boolean(busy)}
        onCancel={() => !busy && setPending(null)}
        onConfirm={async () => {
          const { kind, row } = pending;
          const id = rowId(row);
          await run(
            `${kind}:${id}`,
            () => (kind === 'cms'
              ? appearanceApi.deleteBlock(id)
              : appearanceApi.deleteBanner(id)),
            kind === 'cms' ? 'İçerik alanı silindi' : 'Banner silindi',
          );
          setPending(null);
        }}
      />
    </div>
  );
};

export default EmSportsAppearance;
